/**
 * /onboarding/pending — landed on right after filing an organization
 * request (Onboarding.tsx), and on every subsequent visit/refresh/re-login
 * until a super admin resolves it (see saas-tenant-request.controller.ts).
 * Identity-only route (see App.tsx) — reachable before a tenant exists.
 *
 * Checks the request's current status: still pending → shows the "we're
 * reviewing this" state and re-polls (see POLL_INTERVAL_MS) so an approval
 * lands without a manual refresh; approved → refreshes the token to pick up
 * the new tenant claim and moves them to the dashboard; rejected → shows the
 * reason with a way to submit a new request; no request found at all (e.g.
 * someone navigates here directly without ever submitting one) → sends them
 * back to /onboarding to start one.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, XCircle, ArrowRight } from 'lucide-react';
import { ThemeCtx, useTheme, useThemeState, THEMES, NavBar, Footer } from '../home/marketing-shell';
import { useAuth } from '@/hooks/useAuth';
import type { OrganizationRequestStatus } from '@/services/auth.api';

function PendingCard({ request }: { request: OrganizationRequestStatus }) {
  const { theme } = useTheme();
  const t = THEMES[theme];
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const { logout } = useAuth();
  const isRejected = request.status === 'rejected';

  return (
    <div
      style={{
        background: t.card, border: `1px solid ${t.border}`, borderRadius: 24, padding: '40px 32px',
        display: 'flex', flexDirection: 'column', gap: 18, width: '100%', maxWidth: 440, textAlign: 'center',
      }}
    >
      <div style={{
        width: 52, height: 52, borderRadius: 14,
        background: isRejected ? 'rgba(239,68,68,0.12)' : t.greenDim,
        border: `1px solid ${t.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto',
      }}>
        {isRejected ? <XCircle size={24} color="#ef4444" /> : <Clock size={24} color={t.green} />}
      </div>

      <div>
        <h1 style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: 22, color: t.fg, marginBottom: 8 }}>
          {isRejected ? "This request wasn't approved" : "We're reviewing your request"}
        </h1>
        <p style={{ color: t.mutedFg, fontSize: 14.5, lineHeight: 1.6 }}>
          {isRejected
            ? request.rejectionReason ||
              `${request.organizationName} could not be approved. Contact support for details, or submit a new request.`
            : `We're validating the details for ${request.organizationName} — we'll email you as soon as it's ready. No further action is needed right now.`}
        </p>
      </div>

      {isRejected && (
        <button
          type="button"
          onClick={() => navigate('/onboarding', { replace: true })}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            padding: '14px 24px', borderRadius: 12, border: 'none', cursor: 'pointer',
            background: t.green, color: isDark ? '#050C09' : '#fff', fontWeight: 800, fontSize: 15,
            fontFamily: 'Manrope, sans-serif',
            boxShadow: `0 8px 30px ${isDark ? 'rgba(61,220,132,0.35)' : 'rgba(37,211,102,0.4)'}`,
          }}
        >
          Submit a new request <ArrowRight size={16} />
        </button>
      )}

      {/* This person IS already signed in (identity-only session, no tenant
          yet) — NavBar's "Sign in" link is useless from here: Login.tsx
          auto-redirects any authenticated visitor with no tenant straight
          back to /onboarding, which bounces right back to this same screen.
          Logging out first is the only way to actually reach the login form
          again — e.g. to sign in as a different account. */}
      <p style={{ fontSize: 13, color: t.mutedFg }}>
        Wrong account?{' '}
        <button
          type="button"
          onClick={logout}
          style={{
            background: 'none', border: 'none', padding: 0, cursor: 'pointer',
            color: t.green, fontWeight: 700, fontSize: 13, fontFamily: "'Inter', sans-serif",
            textDecoration: 'underline',
          }}
        >
          Log out
        </button>
      </p>
    </div>
  );
}

/** How often the pending screen re-checks for an approval decision. Only
 *  polls while the request is actually 'pending' — a resolved request stops
 *  the timer immediately. 30s is slow enough to be negligible load (one
 *  indexed lookup per tab) and fast enough that someone watching the screen
 *  during approval isn't left staring at a stale "we're reviewing this". */
const POLL_INTERVAL_MS = 30_000;

function OnboardingPendingContent() {
  const navigate = useNavigate();
  const { getOrganizationRequestStatus, refreshToken } = useAuth();
  const [request, setRequest] = useState<OrganizationRequestStatus | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    async function check() {
      const result = await getOrganizationRequestStatus();
      if (cancelled) return;

      if (!result.success || !result.request) {
        // Nothing to show here — send them to start a real request.
        navigate('/onboarding', { replace: true });
        return;
      }

      // Approved while they were sitting here (or between sessions): the
      // tenant + its database now exist, but the JWT in hand still carries
      // no tenant claim. Refreshing the token re-derives claims from the
      // now-set user.tenantId (see auth.service.ts#refreshToken), which is
      // what lets ProtectedRoute admit them to the dashboard.
      if (result.request.status === 'approved') {
        await refreshToken();
        if (cancelled) return;
        navigate('/admin/dashboard', { replace: true });
        return;
      }

      setRequest(result.request);
      setLoading(false);

      if (result.request.status === 'pending') {
        timer = setTimeout(check, POLL_INTERVAL_MS);
      }
    }

    check();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading || !request) return null;
  return <PendingCard request={request} />;
}

export default function OnboardingPending() {
  const themeCtxValue = useThemeState('light');
  const t = THEMES[themeCtxValue.theme];

  return (
    <ThemeCtx.Provider value={themeCtxValue}>
      <div style={{ minHeight: '100vh', background: t.bg, color: t.fg, fontFamily: "'Inter', sans-serif" }}>
        <NavBar />
        <main style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '130px 24px 80px' }}>
          <OnboardingPendingContent />
        </main>
        <Footer />
      </div>
    </ThemeCtx.Provider>
  );
}
